<script setup>
import { ref, reactive, watch, onMounted, computed } from 'vue';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import axios from 'axios';
import { useToast } from 'primevue/usetoast';
import Calendar from 'primevue/calendar';
import Dropdown from 'primevue/dropdown';

const props = defineProps({
  visible: { type: Boolean, required: true },
  booking: { type: Object, default: null },
});

const emit = defineEmits(['update:visible', 'updated']);

const show = ref(props.visible);
const toast = useToast();
const loading = ref(false);
const providers = ref([]);
const services = ref([]);

const form = reactive({
  providerUuid: '',
  serviceUuid: '',
  startDatetime: null,
  endDatetime: null,
  resourceId: null,
});

const lastLoadedProvider = ref(null);

const loadServices = async (providerUuid) => {
  if (!providerUuid) {
    services.value = [];
    lastLoadedProvider.value = null;
    return;
  }
  
  if (providerUuid === lastLoadedProvider.value) return;
  lastLoadedProvider.value = providerUuid;

  try {
    const params = { all: 1, provider_id: providerUuid };
    if (props.booking?.customer?.gender) {
        params.resource_type_filter = props.booking.customer.gender;
    }
    const { data } = await axios.get('/ajax/admin/services', { params });
    const rows = data?.data || data || [];
    services.value = rows.map(s => ({
      uuid: s.uuid,
      name: s.name,
      duration: s.duration || 0,
      buffer_before: s.buffer_before_minutes || 0,
      buffer_after: s.buffer_after_minutes || 0,
      device_requirements: s.device_requirements || [],
    }));
    
    // If the current service is not in the new provider's list, reset it
    if (form.serviceUuid && !services.value.some(s => s.uuid === form.serviceUuid)) {
        form.serviceUuid = '';
    }
  } catch (e) {
    services.value = [];
    lastLoadedProvider.value = null;
  }
};

const parseAsLocal = (str) => {
  if (!str) return null;
  if (str instanceof Date) return str;
  // Strip 'Z' and everything after seconds, ensure 'T' separator
  let normalized = String(str).replace(' ', 'T').split('.')[0].replace('Z', '');
  const d = new Date(normalized);
  return isNaN(d.getTime()) ? null : d;
};

const initForm = () => {
  if (!props.booking) return;


  // Handle both raw DayPilot data and full Model data
  form.providerUuid = props.booking.provider?.uuid || props.booking.provider_id || '';
  form.serviceUuid = props.booking.service?.uuid || props.booking.service_id || '';
  
  form.startDatetime = parseAsLocal(props.booking.start_datetime);
  form.endDatetime = parseAsLocal(props.booking.end_datetime);
  
  // Try to find the resource id if it was selected previously
  const resourceRes = props.booking.resource_reservations?.[0];
  if (resourceRes && resourceRes.device_resource_id) {
     form.resourceId = resourceRes.device_resource_id;
  } else {
     form.resourceId = null;
  }
};

watch(
    () => [props.visible, props.booking?.uuid || props.booking?.id],
    (newVal, oldVal) => {
        const [isVisible, uuid] = newVal;
        const [oldVisible, oldUuid] = oldVal || [false, null];
        
        show.value = isVisible;
        if (isVisible && (uuid !== oldUuid || !oldVisible)) {
            initForm();
        }
    },
    { immediate: true }
);

watch(show, (v) => {
  emit('update:visible', v);
});

watch(() => form.providerUuid, (newUuid) => {
  if (props.visible && newUuid) {
    loadServices(newUuid);
  }
});


const extractError = (e, defaultMsg = 'Vorgang fehlgeschlagen') => {
    const data = e.response?.data;
    if (data?.errors) {
        const first = Object.values(data.errors).flat()[0];
        if (first) return first;
    }
    return data?.message || e.message || defaultMsg;
};

const toLocalDatetime = (date) => {
  if (!date) return null;
  const d = new Date(date);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

const formatTime = (date) => {
  if (!date) return '--:--';
  if (typeof date === 'string' && date.includes(' ')) {
    return date.split(' ')[1].substring(0, 5);
  }
  const d = date instanceof Date ? date : new Date(date);
  return d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
};

const updateBooking = async () => {
  if (!form.providerUuid || !form.serviceUuid || !form.startDatetime) {
    toast.add({ severity: 'warn', summary: 'Warnung', detail: 'Bitte füllen Sie alle Pflichtfelder aus.', life: 3000 });
    return;
  }

  loading.value = true;
  try {
    const uuid = props.booking.uuid || props.booking.id;
    await axios.patch(`/ajax/admin/bookings/${uuid}/reschedule`, {
      start_datetime: toLocalDatetime(form.startDatetime),
      end_datetime: toLocalDatetime(form.endDatetime),
      provider_id: form.providerUuid,
      service_id: form.serviceUuid,
      resource_id: form.resourceId,
    });

    toast.add({ severity: 'success', summary: 'Erfolg', detail: 'Buchung erfolgreich aktualisiert', life: 3000 });
    emit('updated');
    show.value = false;
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Fehler', detail: extractError(e, 'Aktualisierung fehlgeschlagen'), life: 5000 });
  } finally {
    loading.value = false;
  }
};

const loadProviders = async () => {
  try {
    const { data } = await axios.get('/ajax/admin/providers', { params: { all: 1 } });
    providers.value = data?.data || data || [];
  } catch (e) {}
};

onMounted(() => {
  loadProviders();
});

const selectedService = computed(() => {
  return services.value.find(s => s.uuid === form.serviceUuid) || null;
});

const hasDeviceSelection = computed(() => {
  return selectedService.value?.device_requirements?.some(r => r.device?.allow_resource_selection) || false;
});

const availableResources = ref([]);
const loadingResources = ref(false);

const fetchAvailableResources = async () => {
  if (!form.serviceUuid || !form.startDatetime || !form.endDatetime || !hasDeviceSelection.value) {
    availableResources.value = [];
    if (!props.booking?.resource_reservations?.some(r => r.device_resource_id === form.resourceId)) {
        form.resourceId = null;
    }
    return;
  }
  
  loadingResources.value = true;
  try {
    const d = new Date(form.startDatetime);
    const pad = (n) => String(n).padStart(2, '0');
    const isoStart = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    
    const dEnd = new Date(form.endDatetime);
    const isoEnd = `${dEnd.getFullYear()}-${pad(dEnd.getMonth() + 1)}-${pad(dEnd.getDate())} ${pad(dEnd.getHours())}:${pad(dEnd.getMinutes())}:${pad(dEnd.getSeconds())}`;
    
    const params = { start_datetime: isoStart, end_datetime: isoEnd };
    if (props.booking?.id) {
        params.ignore_booking_id = props.booking.id;
    }

    const { data } = await axios.get(`/ajax/admin/services/${form.serviceUuid}/available-resources`, {
        params
    });
    const allResources = Array.isArray(data) ? data : (data.data || []);
    availableResources.value = allResources.filter(r => r.available !== false);
    
    // If the currently assigned resource is not in the array, add it as a fake entry (to keep it selected in UI if booked in past etc)
    if (form.resourceId && !availableResources.value.some(r => r.resource_id === form.resourceId)) {
       const existingRes = props.booking?.resource_reservations?.find(r => r.device_resource_id === form.resourceId);
       if (existingRes) {
          availableResources.value.push({ resource_id: form.resourceId, name: existingRes.device_resource?.name || existingRes.device?.name || 'Aktuelles Gerät', available: true });
       }
    }

  } catch (e) {
    availableResources.value = [];
    console.error('Failed to fetch available resources', e);
  } finally {
    loadingResources.value = false;
  }
};

watch([() => form.serviceUuid, () => form.startDatetime, () => form.endDatetime, () => hasDeviceSelection.value], fetchAvailableResources, { immediate: true });

const actualDurationMinutes = computed(() => {
  if (form.startDatetime && form.endDatetime) {
    const start = new Date(form.startDatetime);
    const end = new Date(form.endDatetime);
    if (end > start) {
      return (end.getTime() - start.getTime()) / 60000;
    }
  }
  const s = selectedService.value;
  return s?.duration || 30;
});

// Blocked end = start + actualDuration + buffer_after
const formattedEndDatetime = computed(() => {
  if (!form.startDatetime || !form.serviceUuid) return null;
  const s = selectedService.value;
  const duration = actualDurationMinutes.value;
  const bufferAfter = s?.buffer_after || 0;
  const end = new Date(form.startDatetime.getTime() + (duration + bufferAfter) * 60000);
  return toLocalDatetime(end);
});

// blocked_start = start - buffer_before
const blockedStartDatetime = computed(() => {
  if (!form.startDatetime || !selectedService.value) return null;
  const bufferBefore = selectedService.value.buffer_before || 0;
  const d = new Date(form.startDatetime);
  return new Date(d.getTime() - bufferBefore * 60000);
});

const durationBreakdown = computed(() => {
  const s = selectedService.value;
  if (!s) return null;
  const parts = [];
  if (s.buffer_before > 0) parts.push(`${s.buffer_before}m Puffer vorher`);
  parts.push(`${actualDurationMinutes.value}m Service`);
  if (s.buffer_after > 0) parts.push(`${s.buffer_after}m Puffer nachher`);
  const total = (s.buffer_before || 0) + actualDurationMinutes.value + (s.buffer_after || 0);
  return { parts, total };
});


const customerName = computed(() => {
  const c = props.booking?.customer;
  if (!c) return 'Kunde';
  return `${c.first_name || ''} ${c.last_name || ''}`.trim() || 'Kunde';
});
</script>

<template>
  <Dialog 
    v-model:visible="show" 
    modal 
    header="Buchung bearbeiten" 
    :pt="{
      header: { class: '!p-5 !border-b !border-gray-100 !items-center' },
      title: { class: '!text-[0.85rem] !font-bold !text-gray-500 !uppercase !tracking-widest' }
    }"
  >
    <div class="space-y-4">
      <!-- Client (ReadOnly) -->
      <div class="flex flex-col gap-2">
        <label class="font-bold text-gray-500 text-[10px] uppercase tracking-widest">Kunde</label>
        <div class="flex items-center gap-3 p-4 bg-gray-50 border border-gray-100 rounded-2xl">
          <div class="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-500">
             <i class="pi pi-user text-xs"></i>
          </div>
          <span class="text-[13px] font-bold text-gray-800">{{ customerName }}</span>
        </div>
      </div>

      <!-- Provider Selection -->
      <div class="flex flex-col gap-2">
        <label class="font-bold text-gray-500 text-[10px] uppercase tracking-widest">Mitarbeiter *</label>
        <Dropdown 
          v-model="form.providerUuid" 
          :options="providers" 
          optionLabel="name" 
          optionValue="uuid" 
          placeholder="Mitarbeiter wählen"
          class="w-full !rounded-xl !border-gray-200"
          inputClass="!p-3.5 !text-[13px] !font-medium"
        />
      </div>

      <!-- Service Selection -->
      <div class="flex flex-col gap-2">
        <label class="font-bold text-gray-500 text-[10px] uppercase tracking-widest">Service *</label>
        <Dropdown 
          v-model="form.serviceUuid" 
          :options="services" 
          optionLabel="name" 
          optionValue="uuid" 
          placeholder="Service wählen"
          class="w-full !rounded-xl !border-gray-200"
          inputClass="!p-3.5 !text-[13px] !font-medium"
        />
      </div>

      <!-- Time Selection -->
      <div class="grid grid-cols-2 gap-6">
        <div class="flex flex-col gap-2">
          <label class="font-bold text-gray-500 text-[10px] uppercase tracking-widest">Startzeit *</label>
          <Calendar 
            v-model="form.startDatetime" 
            showTime 
            :stepMinute="15"
            hourFormat="24" 
            dateFormat="dd.mm.yy" 
            class="w-full"
            inputClass="p-3.5 border border-gray-200 rounded-xl bg-white text-indigo-600 font-bold focus:ring-2 focus:ring-black outline-none text-[13px]"
          />
        </div>
        <div class="flex flex-col gap-2">
          <label class="font-bold text-gray-500 text-[10px] uppercase tracking-widest">Endzeit *</label>
          <Calendar 
            v-model="form.endDatetime" 
            showTime 
            :stepMinute="15"
            hourFormat="24" 
            dateFormat="dd.mm.yy" 
            class="w-full"
            inputClass="p-3.5 border border-gray-200 rounded-xl bg-white text-indigo-600 font-bold focus:ring-2 focus:ring-black outline-none text-[13px]"
          />
        </div>
      </div>
      <!-- Duration breakdown chips -->
      <div v-if="durationBreakdown" class="flex flex-wrap items-center gap-1.5">
        <template v-for="(part, i) in durationBreakdown.parts" :key="i">
          <span 
            class="text-[10px] font-bold px-2 py-0.5 rounded-full"
            :class="part.includes('Service') ? 'bg-indigo-50 text-indigo-600 border border-indigo-100' : 'bg-orange-50 text-orange-500 border border-orange-100'"
          >{{ part }}</span>
          <span v-if="i < durationBreakdown.parts.length - 1" class="text-gray-300 text-[10px] font-bold">+</span>
        </template>
        <span class="text-[10px] font-black text-gray-500 ml-1">= {{ durationBreakdown.total }}m gesamt</span>
      </div>
      <!-- Blocked window row (only shown when buffer_before > 0) -->
      <div v-if="durationBreakdown && selectedService?.buffer_before > 0" class="flex items-center gap-2 pt-0.5">
        <i class="pi pi-lock text-[9px] text-gray-400"></i>
        <span class="text-[10px] text-gray-400 font-bold">Blockiert:</span>
        <span class="text-[10px] font-black text-gray-600">
          {{ formatTime(blockedStartDatetime) }} → {{ formattedEndDatetime || '--:--' }}
        </span>
        <span class="text-[9px] text-gray-400">({{ durationBreakdown.total }}m gesamt)</span>
      </div>

      <!-- Device Resource Selection -->
      <div v-if="hasDeviceSelection" class="flex flex-col gap-2 pt-4 border-t border-gray-100">
        <label class="font-bold text-gray-500 text-[10px] uppercase tracking-widest">Gerät wählen</label>
        <Dropdown 
          v-model="form.resourceId" 
          :options="availableResources" 
          optionLabel="name" 
          optionValue="resource_id" 
          :optionDisabled="option => !option.available"
          placeholder="Verfügbares Gerät wählen"
          class="w-full !rounded-xl !border-gray-200"
          inputClass="!p-3.5 !text-[13px] !font-medium"
          :loading="loadingResources"
          emptyMessage="Keine Geräte verfügbar"
        >
          <template #option="slotProps">
             <div class="flex items-center justify-between w-full">
                <span>{{ slotProps.option.name }}</span>
                <span v-if="!slotProps.option.available" class="text-xs text-red-500 font-bold ml-2">(Belegt)</span>
             </div>
          </template>
        </Dropdown>
      </div>

      <!-- Footer Buttons -->
      <div class="pt-8 flex gap-4">
        <button 
          type="button"
          @click="show = false" 
          class="text-gray-400 font-bold hover:!bg-sky-50 hover:!text-sky-600 transition-all px-6 py-3 rounded-full flex-1 uppercase tracking-tight text-[11px]"
        >
          Abbrechen
        </button>
        <Button 
          @click="updateBooking" 
          label="Änderungen speichern" 
          :loading="loading"
          class="!rounded-xl !px-10 !py-3.5 !font-bold !bg-black !text-white !border-none flex-1 shadow-xl shadow-black/10 hover:!scale-[1.02] active:!scale-[0.98] transition-all uppercase tracking-widest text-[11px]" 
        />
      </div>
    </div>
  </Dialog>
</template>


